export const ACCEPTED = { type: "accepted", name: "Aceptados", required: false, virtual: true };

export const formatUserName = user => {
  const pi = user?.personal_information || {};
  const name = pi.social_name
    ? `${pi.social_name} ${pi.last_name || ""}`.trim()
    : `${pi.first_name || ""} ${pi.middle_name || ""} ${pi.last_name || ""}`.trim();
  return name || user.email || pi.last_email || "";
};

export const stageOf = (en = {}, steps = []) => {
  const list = en.stages || [];
  if (!list.length) return null;
  const order = Object.fromEntries(steps.map((s, i) => [s.type, i]));
  const sort  = (a, b) => (order[a.stage_type] ?? 9e9) - (order[b.stage_type] ?? 9e9);

  const rejected = [...list].sort(sort).find(s => s.aasm_state === "rejected");
  if (rejected) return rejected;

  const pending = [...list].sort(sort)
    .find(s => ["pending", "editable"].includes(s.aasm_state));
  if (pending) return pending;

  const approved = [...list].filter(s => s.aasm_state === "approved").sort(sort);
  return approved.at(-1) || null;
};

export const isRejected = src =>
  src.aasm_state === "rejected" ||
  src.applicant?.enrollment?.stages?.some(st => st.aasm_state === "rejected");

export const groupByStage = (hits, steps) => {
  const base = Object.fromEntries(steps.map(s => [s.type, []]));
  hits.forEach(h => {
    const st = stageOf(h._source.applicant.enrollment, steps);
    base[st?.stage_type || ACCEPTED.type].push(h);
  });
  return base;
};

// Igual que groupByStage, pero si existe proyección en vuelo (drag),
// forzamos la columna destino para ese item.
export const groupByStageProjected = (hits, steps, projections /* Map hit._id -> {to} */) => {
  const base = Object.fromEntries(steps.map(s => [s.type, []]));
  hits.forEach(h => {
    const proj = projections?.get(h._id);
    if (proj?.to) {
      base[proj.to]?.push(h);
      return;
    }
    const st = stageOf(h._source.applicant.enrollment, steps);
    base[st?.stage_type || ACCEPTED.type].push(h);
  });
  return base;
};

export const canMove = ({ applicant, targetStep, steps }) => {
  if (!targetStep || targetStep.virtual) return false;
  
  const currentStage = stageOf(applicant.enrollment, steps);
  const currentIdx = currentStage 
    ? steps.findIndex(s => s.type === currentStage.stage_type)
    : -1;
  
  const done =
    applicant.enrollment?.stages
      ?.filter(s => s.aasm_state === "approved")
      .map(s => s.stage_type) || [];

  const dstIdx = steps.findIndex(s => s.type === targetStep.type);
  
  if (currentIdx === -1) {
    return dstIdx === 1;
  }

  const startIdx = Math.min(currentIdx, dstIdx);
  const endIdx = Math.max(currentIdx, dstIdx);
  
  const requiredInRange = steps
    .slice(startIdx + 1, endIdx)
    .filter(s => s.required)
    .map(s => s.type);
  
  if (dstIdx > currentIdx) {
    const skippedRequired = requiredInRange.filter(r => !done.includes(r));
    return skippedRequired.length === 0;
  }
  
  if (dstIdx < currentIdx) {
    return true;
  }
  
  return false;
};

export const dispatchClick = el =>
  el && el.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));

// Utilidad para preservar el orden previo de cada columna al mezclar nuevos grupos
export const preserveOrder = (prev, fresh) => {
  const out = {};
  for (const key of Object.keys(fresh)) {
    const prevIds = (prev[key] || []).map(h => h._id);
    const map     = new Map((fresh[key] || []).map(h => [h._id, h]));
    const ordered = [];
    for (const id of prevIds) {
      if (map.has(id)) { ordered.push(map.get(id)); map.delete(id); }
    }
    for (const h of map.values()) ordered.push(h);
    out[key] = ordered;
  }
  return out;
};
